import { UNITS } from 'constants/all';

import { LOCATION_CHANGE } from 'store/app/actions';
import type { RootState } from 'store/configureStore';
import type { ActionWithPayload } from 'store/types';

import type { DisplayUnits } from '../session/sceneUnits';
import { types } from './actions';

const DIMENSIONAL_PRECISION = 'dxfViewer:dimensionalPrecision';
const DISPLAY_UNITS = 'dxfViewer:displayUnits';

/** Last resort when the file declares no unit and the user has expressed none. */
const FALLBACK_DISPLAY_UNITS: DisplayUnits = UNITS.IN;

const readStoredDisplayUnits = (): DisplayUnits | null => {
  const stored = localStorage.getItem(DISPLAY_UNITS);
  return stored === UNITS.IN || stored === UNITS.MM ? stored : null;
};

type DxfViewerState = {
  dimensionalPrecision: number;
  /**
   * What the user has explicitly asked for, or `null` to follow the drawing.
   *
   * Split from the drawing's own unit so that "default to the file's native unit"
   * and "let them change it" are not the same field fighting each other: opening a
   * metric drawing shows millimetres, and a switch to imperial then sticks.
   */
  userDisplayUnits: DisplayUnits | null;
  /** The drawing's native unit, or `null` when its header declares none. */
  sceneDisplayUnits: DisplayUnits | null;
  /** True when the file declares no unit, so values take a neutral "u" suffix. */
  isSceneUnitless: boolean;
};

const initialState = (): DxfViewerState => ({
  // Decimal precision of displayed measurements
  dimensionalPrecision:
    Number(localStorage.getItem(DIMENSIONAL_PRECISION)) || 3,
  userDisplayUnits: readStoredDisplayUnits(),
  sceneDisplayUnits: null,
  isSceneUnitless: false,
});

type DxfViewerAction =
  | ActionWithPayload<typeof LOCATION_CHANGE>
  | ActionWithPayload<
      typeof types.RECEIVE_DISPLAY_UNITS,
      { displayUnits: DisplayUnits }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_SCENE_UNITS,
      { displayUnits: DisplayUnits | null; isUnitless: boolean }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_DIMENSIONAL_PRECISION,
      { dimensionalPrecision: number }
    >;

//initialState is a factory so that state is reset everytime
const reducer = (
  state: DxfViewerState = initialState(),
  action: DxfViewerAction,
): DxfViewerState => {
  switch (action.type) {
    case LOCATION_CHANGE:
      return initialState();
    case types.RECEIVE_DISPLAY_UNITS: {
      localStorage.setItem(DISPLAY_UNITS, action.payload.displayUnits);
      return {
        ...state,
        userDisplayUnits: action.payload.displayUnits,
      };
    }
    case types.RECEIVE_SCENE_UNITS: {
      return {
        ...state,
        sceneDisplayUnits: action.payload.displayUnits,
        isSceneUnitless: action.payload.isUnitless,
      };
    }
    case types.RECEIVE_DIMENSIONAL_PRECISION: {
      localStorage.setItem(
        DIMENSIONAL_PRECISION,
        action.payload.dimensionalPrecision.toString(),
      );
      return {
        ...state,
        dimensionalPrecision: action.payload.dimensionalPrecision,
      };
    }
    default:
      return state;
  }
};

const getScope = (state: RootState) => state.modules.common.viewer.dxfViewer;

/**
 * Units to format in: what the user asked for, else the drawing's own, else a
 * fallback. A unitless drawing still formats numerically — `getIsSceneUnitless`
 * is what drives the neutral suffix.
 */
export const getDisplayUnits = (state: RootState): DisplayUnits => {
  const scope = getScope(state);
  return (
    scope.userDisplayUnits ?? scope.sceneDisplayUnits ?? FALLBACK_DISPLAY_UNITS
  );
};

/** True when the file declared no unit, so values should read as bare "u". */
export const getIsSceneUnitless = (state: RootState) =>
  getScope(state).isSceneUnitless;

/** True when the displayed unit is the drawing's own rather than a user choice. */
export const getIsFollowingSceneUnits = (state: RootState) =>
  getScope(state).userDisplayUnits === null;

export const getDimensionalPrecision = (state: RootState) =>
  getScope(state).dimensionalPrecision;

export default reducer;
